import { AppNS } from 'app';
import axios, { Canceler } from 'axios';

import { API_URL } from '../../core/config';
import { parseProjectLead } from '../client-bookings/client-bookings-parsers';
import { parseJobData } from '../common-parsers/common-parsers';
import { getErrorMessage } from '../helpers/errorHandling';
import { filterPermits, parseMatchingResults, parsePermit, parsePermitsByAddressResults, parsePermitSearchAddress, prepareJobData } from './client-jobs-parsers';

const URL = 'jobs';
const MATCHED = 'jobs/matched-agencies';
const LEADS_URL = 'job_leads';
const PERMIT_URL = 'jobs/permit.json';
const PERMIT_BY_ADDRESS_URL = 'jobs/permits-for-address.json';
const CHECKOUTS = 'checkouts';

const CancelToken = axios.CancelToken;
let cancel: Canceler;
let cancel1: Canceler;

export function apiGetJobById(jobId: string): Promise<AppNS.Project> {
  if (cancel1 !== undefined) {
    cancel1();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${URL}/${jobId}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel1 = c;
        }),
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(parseJobData(data.model));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetProjectLeadById(
  leadId: string
): Promise<AppNS.ProjectLead> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${LEADS_URL}/${leadId}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(parseProjectLead(data.model));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetPermitsByAddress(
  address: AppNS.ProjectAddress
): Promise<{ results: Array<AppNS.Permit>; state: string }> {
  if (cancel1 !== undefined) {
    cancel1();
  }

  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${PERMIT_BY_ADDRESS_URL}`, {
        params: { address: parsePermitSearchAddress(address) },
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel1 = c;
        }),
      })
      .then(({ data }) => {
        if (data.state) {
          resolve({
            results: parsePermitsByAddressResults(data.results),
            state: data.state,
          });
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetPermitsById(
  ids: Array<string>,
  jobType: AppNS.ProjectType
): Promise<Array<AppNS.Permit>> {
  const promises = ids.map((id: string) =>
    apiGetPermitById(id.trim(), jobType)
  );
  return Promise.all(promises).then(filterPermits);
}

export function apiGetPermitById(
  id: string,
  jobType: AppNS.ProjectType
): Promise<AppNS.Permit | undefined> {
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${PERMIT_URL}`, {
        params: { permit_id: id, job_type: jobType },
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(parsePermit(data.model, jobType));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetNewJobResults(
  jobId: string
): Promise<Array<AppNS.MatchingResult>> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${MATCHED}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
        params: { id: jobId },
      })
      .then(({ data }) => {
        if (data) {
          resolve(parseMatchingResults(data.results.agencies));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSendNewJob(reqData: AppNS.Project): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${URL}.json`, prepareJobData(reqData))
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject(data);
        }
      })
      .catch((error) => {
        reject(error.response.data);
      });
  });
}

export function apiBookLead(
  lead: AppNS.ProjectLead,
  clientInvoiceData: string
): Promise<string> {
  const reqData = {
    properties: {
      clientInvoiceData,
      jobLeadId: lead.id,
      paymentMethodId: lead?.paymentMethodId,
    },
  };

  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${CHECKOUTS}`, reqData)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function apiGetCheckoutState(checkoutId: string): Promise<AppNS.Params> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${CHECKOUTS}/${checkoutId}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function apiAskForProposal(
  jobId: string,
  agencyCompanyId: string,
  type: AppNS.ProjectLeadType
): Promise<string> {
  const reqData = {
    properties: {
      jobId,
      agencyCompanyId,
      type,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/job_leads.json`, reqData)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiCheckIfProposalRequested(
  jobId: string,
  agencyCompanyId: string
): Promise<AppNS.Params | null> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${LEADS_URL}/is-invited.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
        params: { job_id: jobId, agency_company_id: agencyCompanyId },
      })
      .then(({ data }) => {
        if (data) {
          resolve(data?.model ? data.model : null);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}
